import React, { useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { ChevronDown, ChevronLeft, PanelLeftClose, PanelLeftOpen } from "lucide-react";
import { ScrollArea } from "../ui/scroll-area";
import { Badge } from "../ui/badge";
import {
  Collapsible,
  CollapsibleTrigger,
  CollapsibleContent,
} from "../ui/collapsible";
import { Separator } from "../ui/separator";
import useMediaQuery from "../shared/useMediaQuery";
import { SIDEBAR_SECTIONS } from "./sidebarNavigation";
import UserProfileCard from "./UserProfileCard";
import RscLogo from "../../assests/logo/rscLogo copy";

// Icon glyphs matching the artifact's own rail nav (`.i` spans) 1:1 — plain
// unicode symbols for most rows, real emoji for a few (My Card, In-Store
// Flips, Shipments, Wallet & Gift Cards, Condition & Damages, Pre-Quoted
// Cooks). Rendered as text rather than swapped for lucide components.
function NavIcon({ glyph, active, collapsed = false }) {
  return (
    <span
      aria-hidden="true"
      style={{
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        width: collapsed ? 20 : 18,
        flexShrink: 0,
        marginRight: collapsed ? 0 : 10,
        fontSize: collapsed ? 16 : 14,
        lineHeight: 1,
        color: active ? "#00ADB5" : "rgba(255,255,255,0.55)",
      }}
    >
      {glyph}
    </span>
  );
}

// Always rendered (never unmounted) so the sidebar-collapse toggle animates
// text away smoothly instead of popping instantly — width/opacity transition
// in sync with the aside's own width transition in MemberLayout.
function SidebarLabel({ collapsed, children, maxWidth = 180, style, className }) {
  return (
    <span
      className={className}
      style={{
        display: "inline-block",
        opacity: collapsed ? 0 : 1,
        maxWidth: collapsed ? 0 : maxWidth,
        overflow: "hidden",
        whiteSpace: "nowrap",
        textOverflow: "ellipsis",
        transition: "opacity 0.16s ease, max-width 0.22s ease",
        ...style,
      }}
    >
      {children}
    </span>
  );
}

function NavItem({ item, isActive, onClick, collapsed }) {
  // Tailwind `hover:` classes here used to be dead — inline `style.color`/
  // `style.background` always wins the cascade over a class, so hovering an
  // inactive row never brightened its text. Driving hover from React state
  // instead (single source of truth) is the same technique already used
  // correctly on the collapse-toggle button below.
  const [hovered, setHovered] = useState(false);
  return (
    <button
      onClick={onClick}
      title={collapsed ? item.title : undefined}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      className={`tw-group tw-flex tw-items-center tw-gap-2.5 tw-rounded-lg tw-px-3 tw-py-2 tw-text-[13px] tw-font-medium tw-transition-all tw-duration-200 tw-border tw-border-transparent tw-cursor-pointer tw-bg-transparent tw-text-left${collapsed ? "" : " tw-w-full"}`}
      style={{
        display: "flex",
        width: collapsed ? 36 : "100%",
        minWidth: collapsed ? 36 : undefined,
        height: collapsed ? 36 : undefined,
        minHeight: collapsed ? 36 : undefined,
        alignSelf: collapsed ? "center" : undefined,
        alignItems: "center",
        justifyContent: collapsed ? "center" : "flex-start",
        gap: collapsed ? 0 : undefined,
        borderRadius: 8,
        padding: collapsed ? 0 : "8px 12px",
        boxSizing: "border-box",
        fontSize: 13,
        fontWeight: 500,
        border: isActive ? "1px solid rgba(0,173,181,0.25)" : "1px solid transparent",
        cursor: "pointer",
        background: isActive ? "rgba(0,173,181,0.12)" : hovered ? "rgba(255,255,255,0.05)" : "transparent",
        color: isActive ? "#fff" : hovered ? "#fff" : "rgba(255,255,255,0.92)",
        textAlign: "left",
        transition: "background 0.15s, border-color 0.15s, color 0.15s",
      }}
    >
      <NavIcon glyph={item.icon} active={isActive} collapsed={collapsed} />
      <SidebarLabel collapsed={collapsed} className="tw-truncate">{item.title}</SidebarLabel>
      {item.badge && (
        <SidebarLabel collapsed={collapsed} maxWidth={56} style={{ marginLeft: "auto" }}>
          <Badge variant="new">{item.badge}</Badge>
        </SidebarLabel>
      )}
    </button>
  );
}

function SubNavItem({ active, onClick, children }) {
  const [hovered, setHovered] = useState(false);
  return (
    <button
      onClick={onClick}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      className="tw-flex tw-w-full tw-items-center tw-rounded-md tw-px-2.5 tw-py-1.5 tw-text-[12px] tw-font-medium tw-transition-colors tw-cursor-pointer tw-bg-transparent tw-border-none tw-text-left"
      style={{
        display: "flex",
        width: "100%",
        alignItems: "center",
        borderRadius: 6,
        padding: "7px 10px",
        marginTop: 2,
        fontSize: 13,
        fontWeight: 500,
        cursor: "pointer",
        background: active ? "rgba(0,173,181,0.1)" : hovered ? "rgba(255,255,255,0.04)" : "transparent",
        border: "none",
        color: active ? "#22d3ee" : hovered ? "#fff" : "rgba(255,255,255,0.88)",
        textAlign: "left",
      }}
    >
      {children}
    </button>
  );
}

function ExpandableNavItem({ item, location, navigate, onNavigate, collapsed }) {
  const isParentActive =
    location.pathname === item.path ||
    item.children?.some((c) => location.pathname.startsWith(c.path));
  const [open, setOpen] = useState(isParentActive);
  const [hovered, setHovered] = useState(false);

  // Collapsed rail: clicking the parent just navigates to its default page,
  // same as a plain NavItem — there's no room to show a submenu.
  if (collapsed) {
    return (
      <NavItem
        item={item}
        isActive={isParentActive}
        onClick={() => navigate(item.path)}
        collapsed
      />
    );
  }

  return (
    <Collapsible open={open} onOpenChange={setOpen}>
      <CollapsibleTrigger asChild>
        <button
          onMouseEnter={() => setHovered(true)}
          onMouseLeave={() => setHovered(false)}
          className="tw-group tw-flex tw-w-full tw-items-center tw-gap-2.5 tw-rounded-lg tw-px-3 tw-py-2 tw-text-[13px] tw-font-medium tw-transition-all tw-duration-200 tw-border tw-border-transparent tw-cursor-pointer tw-bg-transparent tw-text-left"
          style={{
            display: "flex",
            width: "100%",
            alignItems: "center",
            borderRadius: 8,
            padding: "8px 12px",
            fontSize: 13,
            fontWeight: 500,
            border: isParentActive ? "1px solid rgba(0,173,181,0.25)" : "1px solid transparent",
            cursor: "pointer",
            background: isParentActive ? "rgba(0,173,181,0.12)" : hovered ? "rgba(255,255,255,0.05)" : "transparent",
            color: isParentActive ? "#fff" : hovered ? "#fff" : "rgba(255,255,255,0.92)",
            textAlign: "left",
          }}
        >
          <NavIcon glyph={item.icon} active={isParentActive} />
          <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} className="tw-truncate">{item.title}</span>
          {item.badge && (
            <Badge variant="new" className="tw-ml-auto tw-mr-1" style={{ marginLeft: "auto", marginRight: 4 }}>
              {item.badge}
            </Badge>
          )}
          <ChevronDown
            size={14}
            className="tw-ml-auto tw-shrink-0 tw-transition-transform tw-duration-200"
            style={{
              marginLeft: "auto",
              flexShrink: 0,
              color: hovered ? "rgba(255,255,255,0.55)" : "rgba(255,255,255,0.35)",
              transition: "transform 0.2s, color 0.15s",
              transform: open ? "rotate(180deg)" : "none",
            }}
          />
        </button>
      </CollapsibleTrigger>
      <CollapsibleContent className="sidebar-collapsible-content">
        <div
          className="tw-ml-[22px] tw-border-l tw-border-white/[0.06] tw-pl-2.5 tw-mt-0.5 tw-mb-1 tw-flex tw-flex-col"
          style={{ marginLeft: 22, borderLeft: "1px solid rgba(255,255,255,0.06)", paddingLeft: 10, marginTop: 2, marginBottom: 4, display: "flex", flexDirection: "column" }}
        >
          {item.children.map((child) => (
            <SubNavItem
              key={child.path}
              active={location.pathname === child.path}
              onClick={() => {
                navigate(child.path);
                onNavigate?.();
              }}
            >
              {child.title}
            </SubNavItem>
          ))}
        </div>
      </CollapsibleContent>
    </Collapsible>
  );
}

export default function MemberSidebar({ onNavigate, collapsed = false, onToggleCollapse }) {
  const location = useLocation();
  const navigate = useNavigate();
  const isTablet = useMediaQuery("(max-width: 960px)");
  const isMobile = useMediaQuery("(max-width: 640px)");
  const [logoutHovered, setLogoutHovered] = useState(false);

  const handleNav = (path) => {
    navigate(path);
    onNavigate?.();
  };

  const handleLogout = () => {
    sessionStorage.clear();
    localStorage.clear();
    const logoutChannel = new BroadcastChannel("logout_channel");
    logoutChannel.postMessage("logout");
    navigate("/");
  };

  return (
    <div
      className="tw-flex tw-flex-col tw-h-full tw-bg-black tw-border-r tw-border-white/[0.06]"
      style={{ display: "flex", flexDirection: "column", height: "100%", width: "100%", background: "#000", borderRight: "1px solid rgba(255,255,255,0.06)" }}
    >
      {/* Branding */}
      <div
        className="tw-flex tw-items-center tw-px-3 tw-py-4 tw-pb-2"
        style={{
          display: "flex",
          flexDirection: "row",
          alignItems: "center",
          justifyContent: collapsed ? "center" : "space-between",
          gap: collapsed ? 2 : 0,
          padding: collapsed ? "12px 8px 8px" : "16px 12px 8px",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", minWidth: 0 }}>
          <div
            className="tw-flex tw-items-center tw-justify-center tw-w-8 tw-h-8 tw-rounded-lg"
            style={{ display: "flex", alignItems: "center", justifyContent: "center", width: collapsed && isTablet ? 20 : 32, height: collapsed && isTablet ? 20 : 32, marginRight: collapsed ? 0 : 10, flexShrink: 0, borderRadius: 8, background: "linear-gradient(135deg, #00ADB5, #22d3ee)" }}
          >
            <RscLogo width="18" height="10" fill="none" color="#001" padding="0" borderRadius="0" />
          </div>
          <SidebarLabel
            collapsed={collapsed}
            maxWidth={60}
            className="tw-text-xs tw-font-extrabold tw-tracking-[0.16em] tw-text-white"
            style={{ fontSize: 12, fontWeight: 800, letterSpacing: "0.16em", color: "#fff" }}
          >
            RSC
          </SidebarLabel>
        </div>

        {(onToggleCollapse || (isMobile && onNavigate)) && (
          <button
            type="button"
            onClick={isMobile && onNavigate ? onNavigate : onToggleCollapse}
            title={isMobile && onNavigate ? "Close navigation" : collapsed ? "Expand sidebar" : "Collapse sidebar"}
            aria-label={isMobile && onNavigate ? "Close navigation" : collapsed ? "Expand sidebar" : "Collapse sidebar"}
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              width: collapsed ? (isTablet ? 20 : 32) : 34,
              height: collapsed ? (isTablet ? 20 : 32) : 34,
              flexShrink: 0,
              borderRadius: 10,
              background: "transparent",
              border: "none",
              // Same blue as the RSC badge above (linear-gradient(135deg,
              // #00ADB5, #22d3ee)) — the lighter end of it, so it reads on a
              // near-black background without needing a tinted pill behind it.
              color: "#22d3ee",
              cursor: "pointer",
              transition: "color 0.15s ease, background 0.15s ease",
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.color = "#fff";
              e.currentTarget.style.background = "rgba(34,211,238,0.1)";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.color = "#22d3ee";
              e.currentTarget.style.background = "transparent";
            }}
          >
            {isMobile && onNavigate ? <ChevronLeft size={18} strokeWidth={2} /> : collapsed ? <PanelLeftOpen size={17} strokeWidth={2} /> : <PanelLeftClose size={17} strokeWidth={2} />}
          </button>
        )}
      </div>

      {/* Navigation */}
      <ScrollArea
        className="tw-flex-1"
        style={{ flex: "1 1 0%", minHeight: 0 }}
      >
        <div
          className="tw-py-1"
          style={{
            width: "100%",
            boxSizing: "border-box",
            padding: collapsed ? "4px 8px" : "4px 10px",
          }}
        >
          {SIDEBAR_SECTIONS.map((section) => (
            <div key={section.label} className="tw-mb-1" style={{ marginBottom: 4 }}>
              <div
                className="tw-px-3 tw-text-[9px] tw-font-black tw-tracking-[0.18em] tw-select-none"
                style={{
                  padding: collapsed ? "0 12px" : "16px 12px 6px",
                  maxHeight: collapsed ? 0 : 40,
                  opacity: collapsed ? 0 : 1,
                  overflow: "hidden",
                  fontSize: 9,
                  fontWeight: 900,
                  letterSpacing: "0.18em",
                  color: "rgba(255,255,255,0.35)",
                  userSelect: "none",
                  textTransform: "uppercase",
                  transition: "max-height 0.2s ease, opacity 0.15s ease, padding 0.2s ease",
                }}
              >
                {section.label}
              </div>
              <div className="tw-flex tw-flex-col" style={{ display: "flex", flexDirection: "column" }}>
                {section.items.map((item, itemIndex) => {
                  const itemStyle = itemIndex > 0 ? { marginTop: 2 } : undefined;
                  if (item.children) {
                    return (
                      <div key={item.path} style={itemStyle}>
                        <ExpandableNavItem
                          item={item}
                          location={location}
                          navigate={navigate}
                          onNavigate={onNavigate}
                          collapsed={collapsed}
                        />
                      </div>
                    );
                  }
                  const isActive =
                    location.pathname === item.path ||
                    (item.path !== "/user-dashboard" &&
                      location.pathname.startsWith(item.path + "/"));
                  return (
                    <div key={item.path} style={itemStyle}>
                      <NavItem
                        item={item}
                        isActive={isActive}
                        onClick={() => handleNav(item.path)}
                        collapsed={collapsed}
                      />
                    </div>
                  );
                })}
              </div>
            </div>
          ))}

          {/* Logout */}
          <div className="tw-mt-1 tw-mb-2" style={{ marginTop: 4, marginBottom: 8 }}>
            <button
              onClick={handleLogout}
              title={collapsed ? "Logout" : undefined}
              onMouseEnter={() => setLogoutHovered(true)}
              onMouseLeave={() => setLogoutHovered(false)}
              className="tw-group tw-flex tw-w-full tw-items-center tw-gap-2.5 tw-rounded-lg tw-px-3 tw-py-2 tw-text-[13px] tw-font-medium tw-transition-colors tw-cursor-pointer tw-bg-transparent tw-border-none tw-text-left"
              style={{
                display: "flex",
                width: collapsed ? 36 : "100%",
                minWidth: collapsed ? 36 : undefined,
                height: collapsed ? 36 : undefined,
                minHeight: collapsed ? 36 : undefined,
                alignSelf: collapsed ? "center" : undefined,
                alignItems: "center",
                justifyContent: collapsed ? "center" : "flex-start",
                gap: collapsed ? 0 : undefined,
                borderRadius: 8,
                padding: collapsed ? 0 : "8px 12px",
                boxSizing: "border-box",
                fontSize: 13,
                fontWeight: 500,
                color: logoutHovered ? "#f27059" : "rgba(242,112,89,0.82)",
                cursor: "pointer",
                background: logoutHovered ? "rgba(242,112,89,0.07)" : "transparent",
                border: "none",
                textAlign: "left",
              }}
            >
              <span aria-hidden="true" style={{ display: "inline-flex", alignItems: "center", justifyContent: "center", width: collapsed ? 20 : 18, flexShrink: 0, marginRight: collapsed ? 0 : 10, fontSize: collapsed ? 16 : 14 }}>
                ⎋
              </span>
              <SidebarLabel collapsed={collapsed} maxWidth={80}>Logout</SidebarLabel>
            </button>
          </div>
        </div>
      </ScrollArea>

      {/* User profile card */}
      <Separator />
      <UserProfileCard collapsed={collapsed} onNavigate={onNavigate} />
    </div>
  );
}
